import React from "react"

import * as Geo from "@/base/geo"
import * as Models from "@/redux/models"

import { Marker } from "@react-google-maps/api"

import {
  getDeviceStatusDataByDeviceIdFromState,
  getMarkerImageSourceByDeviceIdFromState,
} from "@/redux/device-event-last.reducer"
import type { DeviceIdProps } from "@/redux/models"
import { useRootSelector } from "@/redux/useRootSelector"
import { Theme } from "@/theme"
import type { MarkerProps } from "@react-google-maps/api"

import { ArrowBaseWeb } from "./DirectionArrowWeb"
import LinearPathVisualizationWeb from "./LinearPathVisualizationWeb"
import { makeUseDeviceMarkerArrow } from "./makeUseDirectionArrow"

interface DeviceMarkerProps extends Models.DeviceIdProps {
  isAnySelected?: boolean
  isClickable?: boolean
  isHidden?: boolean
  isSelected?: boolean
  onSelectDeviceActivity?: (params: DeviceIdProps) => void
  onSelectDeviceEvent?: Models.SelectDeviceHandler
  showCenterPivotOutline?: boolean
  showDeviceActivityVisualizations: boolean
  showParkingSpots: boolean
}

const ARROW_LENGTH_METERS = 200

const useDirectionArrow = makeUseDeviceMarkerArrow(Geo.pointToGmaps)

/**
 *
 */
export default function DeviceMarkerWeb({
  deviceId,
  isAnySelected,
  isClickable = true,
  isHidden = false,
  isSelected,
  onSelectDeviceEvent,
  showDeviceActivityVisualizations,
}: DeviceMarkerProps): React.JSX.Element | null {
  const deviceStatusData = useRootSelector((state) => {
    return getDeviceStatusDataByDeviceIdFromState(state, deviceId)
  })
  const deviceActivity = useRootSelector((state) => {
    return Models.deviceActivity.selectById(state, deviceId)
  })

  const {
    codaDeviceAlias,
    deviceInstallationType,
    gpsHeading,
    gpsLocation,
    isInMotion = false,
    isStoppedShort,
    isUpdating = false,
  } = deviceStatusData ?? {}

  let animation: google.maps.Animation | undefined
  let fillOpacity = 1
  let markerTitle = codaDeviceAlias
  let strokeColor: string = Theme.COLORS.$black
  let strokeWeight = 1

  if (isSelected === true) {
    animation = 1
    strokeColor = Theme.COLORS.$cyan[500]
    strokeWeight = 3
  } else if (isAnySelected === true) {
    fillOpacity = 0.8
    strokeWeight = 0
  }
  if (isUpdating) {
    animation = 1
  }

  // TODO: We can get rid of the text here and just use the icon

  if (deviceInstallationType === "unconfigured" || isStoppedShort === true) {
    markerTitle += "-warning"
  }

  const uri = useRootSelector((state) =>
    getMarkerImageSourceByDeviceIdFromState(state, deviceId),
  )

  const options = React.useMemo((): Pick<MarkerProps, "icon"> & {
    marker: google.maps.MarkerOptions
  } => {
    const visible = !isHidden
    const markerSizeFactor = 10
    return {
      icon: {
        fillOpacity,
        scaledSize: new google.maps.Size(
          5 * markerSizeFactor,
          6 * markerSizeFactor,
        ),
        strokeColor,
        strokeWeight,
        url: uri,
      },
      marker: { visible },
    }
  }, [fillOpacity, isHidden, strokeColor, strokeWeight, uri])

  const arrowPath = useDirectionArrow({
    center: gpsLocation?.web,
    deviceInstallationType,
    direction: gpsHeading,
    isInMotion,
    lengthMm: ARROW_LENGTH_METERS * 1000,
  })

  /**
   * From the configuration - outlines of pivots, parking spots for linears etc.
   */
  let configuredActivityPathElement: React.JSX.Element | null = null
  /**
   * From the device activity - current pivot path/linear completion etc.
   */
  let deviceActivityElement: React.JSX.Element | null = null

  /**
   * Marker for device event
   */
  let markerElement: React.JSX.Element | null = null

  if (gpsLocation?.web) {
    markerElement = (
      <Marker
        animation={animation}
        clickable={isClickable}
        icon={options.icon}
        position={gpsLocation.web}
        title={markerTitle}
        onClick={
          onSelectDeviceEvent && typeof codaDeviceAlias === "string"
            ? () => {
                onSelectDeviceEvent({ codaDeviceAlias, deviceId, gpsLocation })
              }
            : undefined
        }
      />
    )
  }

  switch (deviceInstallationType) {
    // Legacy `center_pivot` installation type retired in migration 0264; the
    // outline and activity visualizations for pivot monitors now render via
    // the CenterPivotMarker/Web components keyed on pivotId.
    case "linear_move": {
      // if (showParkingSpots && configuration) {
      //   configuredActivityPathElement = (
      //     <LinearSwathOutlineVisualizationWeb
      //       isClickable={isClickable}
      //       linearPathStops={configuration.linearPath}
      //       linearSpanHeadingDegrees={configuration.linearSpanHeadingDegrees}
      //       linearSpanWidthMm={configuration.linearSpanWidthMm}
      //     />
      //   )
      // }
      if (deviceActivity && showDeviceActivityVisualizations) {
        deviceActivityElement = (
          <LinearPathVisualizationWeb
            isClickable={isClickable}
            showParkingSpots={false}
            {...deviceActivity}
          />
        )
      }

      break
    }

    case "reel":
    case "reel_with_booster_off_only": {
      if (arrowPath) {
        deviceActivityElement = (
          <ArrowBaseWeb
            {...arrowPath}
            strokeColor={Theme.COLORS.$reelRunSwath.arrow}
          />
        )
      }
      break
    }
    case "linear_move_controller":
    case "linear_move_monitor":
    case "traveller_soft":
    case "prototype":
    case "pump":
    case "pump_off_only":
    case "pump_on_off":
    case "pump_vfd":
    case "unconfigured":
    case "valve":
    case undefined: {
      break
    }
  }

  if (!showDeviceActivityVisualizations) {
    return null
  }

  return (
    <React.Fragment>
      {configuredActivityPathElement}
      {deviceActivityElement}
      {markerElement}
    </React.Fragment>
  )
}
